import { useEffect, useState } from "react";

const roleDescriptions = {
  Admin: "Can manage workspace settings, users and content.",
  Editor: "Can create and edit content, but cannot manage workspace users.",
  Viewer: "Can view workspace information but cannot make changes.",
};

export default function RoleAssignmentModal({
  show,
  user,
  onClose,
  onSave,
}) {
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (show && user) {
      setRole(user.role);
      setLoading(false);
      setError("");
      setSuccess(false);
    }
  }, [show, user]);

  if (!show || !user) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!role) {
      setError("Please select a role.");
      return;
    }

    if (role === user.role) {
      setError("Please select a different role before saving.");
      return;
    }

    setLoading(true);

    try {
      await onSave(user.id, role);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Unable to update the role.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="role-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div
        className="role-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-modal-title"
      >
        <div className="role-modal-header">
          <div>
            <div className="small text-primary fw-semibold mb-1">
              TEAM / ROLE ASSIGNMENT
            </div>
            <h5 id="role-modal-title" className="mb-0">
              Change user role
            </h5>
          </div>

          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
            disabled={loading}
          />
        </div>

        {success ? (
          <div className="role-modal-body">
            <div className="role-success-icon">
              <i className="bi bi-check-lg" />
            </div>

            <h5 className="text-center">Role updated successfully</h5>
            <p className="text-muted text-center mb-4">
              {user.email} now has the {role} role.
            </p>

            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="role-modal-body">
              <div className="role-user-summary mb-4">
                <div className="team-avatar">
                  {user.email
                    .split("@")[0]
                    .slice(0, 2)
                    .toUpperCase()}
                </div>

                <div>
                  <div className="fw-semibold">{user.name}</div>
                  <div className="small text-muted">{user.email}</div>
                </div>
              </div>

              <label htmlFor="assignedRole" className="form-label fw-semibold">
                Assign role <span className="text-danger">*</span>
              </label>

              <select
                id="assignedRole"
                className={`form-select ${error ? "is-invalid" : ""}`}
                value={role}
                onChange={(event) => {
                  setRole(event.target.value);
                  setError("");
                }}
                disabled={loading}
                required
              >
                <option value="">Select a role</option>
                <option value="Admin">Admin</option>
                <option value="Editor">Editor</option>
                <option value="Viewer">Viewer</option>
              </select>

              {role && (
                <div className="role-description mt-3">
                  <i className="bi bi-info-circle me-2 text-primary" />
                  {roleDescriptions[role]}
                </div>
              )}

              {error && (
                <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                  {error}
                </div>
              )}

              <div className="small text-muted mt-3">
                The selected role controls what this user can access in the
                workspace.
              </div>
            </div>

            <div className="role-modal-footer">
              <button
                type="button"
                className="btn btn-light border"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !role}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  "Save role"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}